import {useState, useEffect, useCallback} from "react"
import {alertsService} from "@/services/api/endpoints/alertsApi"
import {
  AlertType,
  AlertsResponse,
  AlertsStats,
  AlertDetails,
} from "@/features/alerts/alertTypes"
import {
  AlertReviewAction,
  AlertActivationStatus,
} from "@/features/alerts/alertTypes"

/**
 * Hook to use alerts data from Supabase with manual refresh
 */
export function useAlerts() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const [data, setData] = useState<AlertsResponse | null>(null)

  const fetchAlerts = async () => {
    try {
      setLoading(true)
      setError(undefined)
      const alertsData = await alertsService.getAlerts()
      setData(alertsData)
    } catch (err) {
      console.error("Error fetching alerts:", err)
      setError(err instanceof Error ? err.message : "Failed to load alerts")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAlerts()
  }, [])

  const refetch = async () => {
    await fetchAlerts()
  }

  return {
    loading,
    error,
    data: data ? [...data.unresolvedAlerts, ...data.resolvedAlerts] : [],
    unresolvedAlerts: data?.unresolvedAlerts || [],
    resolvedAlerts: data?.resolvedAlerts || [],
    totalCount: data?.totalCount || 0,
    unresolvedCount: data?.unresolvedCount || 0,
    resolvedCount: data?.resolvedCount || 0,
    refetch,
  }
}

/**
 * Enhanced hook for alerts with optimistic updates and per-item loading states
 */
export function useOptimisticAlerts(
  onStatusChange?: (
    fromStatus: AlertActivationStatus,
    toStatus: AlertActivationStatus
  ) => void
) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const [data, setData] = useState<AlertsResponse | null>(null)
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set())

  const fetchAlerts = async (skipLoadingState = false) => {
    try {
      if (!skipLoadingState) {
        setLoading(true)
      }
      setError(undefined)
      const alertsData = await alertsService.getAlerts()
      setData(alertsData)
    } catch (err) {
      console.error("Error fetching alerts:", err)
      setError(err instanceof Error ? err.message : "Failed to load alerts")
    } finally {
      if (!skipLoadingState) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    fetchAlerts()
  }, [])

  const setItemUpdating = useCallback(
    (alertId: string, isUpdating: boolean) => {
      setUpdatingItems((prev) => {
        const newSet = new Set(prev)
        if (isUpdating) {
          newSet.add(alertId)
        } else {
          newSet.delete(alertId)
        }
        return newSet
      })
    },
    []
  )

  const updateAlertOptimistically = useCallback(
    (alertId: string, updates: Partial<AlertType>) => {
      setData((prev) => {
        if (!prev) return prev

        const updateAlertInList = (alerts: AlertType[]) =>
          alerts.map((alert) =>
            alert.id === alertId ? {...alert, ...updates} : alert
          )

        return {
          ...prev,
          unresolvedAlerts: updateAlertInList(prev.unresolvedAlerts),
          resolvedAlerts: updateAlertInList(prev.resolvedAlerts),
        }
      })
    },
    []
  )

  const moveAlertBetweenLists = useCallback(
    (
      alertId: string,
      fromStatus: AlertActivationStatus,
      toStatus: AlertActivationStatus,
      updates: Partial<AlertType> = {}
    ) => {
      setData((prev) => {
        if (!prev) return prev

        // Find the alert in the appropriate list
        let alertToMove: AlertType | null = null
        let updatedUnresolved = [...prev.unresolvedAlerts]
        let updatedResolved = [...prev.resolvedAlerts]

        // Remove from source list
        if (fromStatus === "resolved" || fromStatus === "dismissed") {
          const index = updatedResolved.findIndex(
            (alert) => alert.id === alertId
          )
          if (index !== -1) {
            alertToMove = updatedResolved[index]
            updatedResolved.splice(index, 1)
          }
        } else {
          const index = updatedUnresolved.findIndex(
            (alert) => alert.id === alertId
          )
          if (index !== -1) {
            alertToMove = updatedUnresolved[index]
            updatedUnresolved.splice(index, 1)
          }
        }

        if (!alertToMove) {
          return prev
        }

        // Apply updates to the alert
        const updatedAlert: AlertType = {
          ...alertToMove,
          ...updates,
          status: toStatus,
        }

        // Add to target list
        if (toStatus === "resolved" || toStatus === "dismissed") {
          updatedResolved.unshift(updatedAlert) // Add to beginning of resolved list
        } else {
          updatedUnresolved.unshift(updatedAlert) // Add to beginning of unresolved list
        }

        const newData = {
          ...prev,
          unresolvedAlerts: updatedUnresolved,
          resolvedAlerts: updatedResolved,
          unresolvedCount: updatedUnresolved.length,
          resolvedCount: updatedResolved.length,
        }

        // Notify parent about the status change for optimistic stats updates
        onStatusChange?.(fromStatus, toStatus)

        return newData
      })
    },
    [onStatusChange]
  )

  const refetch = async () => {
    await fetchAlerts()
  }

  const refetchInBackground = async () => {
    await fetchAlerts(true) // Skip loading state to avoid UI flash
  }

  return {
    loading,
    error,
    data: data ? [...data.unresolvedAlerts, ...data.resolvedAlerts] : [],
    unresolvedAlerts: data?.unresolvedAlerts || [],
    resolvedAlerts: data?.resolvedAlerts || [],
    totalCount: data?.totalCount || 0,
    unresolvedCount: data?.unresolvedCount || 0,
    resolvedCount: data?.resolvedCount || 0,
    updatingItems,
    refetch,
    refetchInBackground,
    setItemUpdating,
    updateAlertOptimistically,
    moveAlertBetweenLists,
  }
}

/**
 * Hook to use alert statistics with manual refresh and optimistic updates
 */
export function useAlertStats() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const [stats, setStats] = useState<AlertsStats | null>(null)

  const fetchStats = async () => {
    try {
      setLoading(true)
      setError(undefined)
      const statsData = await alertsService.getAlertStats()
      setStats(statsData)
    } catch (err) {
      console.error("Error fetching alert stats:", err)
      setError(
        err instanceof Error ? err.message : "Failed to load alert statistics"
      )
    } finally {
      setLoading(false)
    }
  }

  const updateStatsOptimistically = useCallback(
    (fromStatus: AlertActivationStatus, toStatus: AlertActivationStatus) => {
      setStats((prev) => {
        if (!prev) return prev

        let unresolvedDelta = 0
        let resolvedDelta = 0
        let escalatedDelta = 0

        // Determine if leaving unresolved status (anything that's not resolved/dismissed)
        if (fromStatus !== "resolved" && fromStatus !== "dismissed") {
          unresolvedDelta -= 1
        } else {
          // Leaving resolved status
          resolvedDelta -= 1
        }

        // Determine if entering resolved status
        if (toStatus === "resolved" || toStatus === "dismissed") {
          resolvedDelta += 1
        } else {
          // Entering unresolved status
          unresolvedDelta += 1
        }

        // Handle escalated status specifically
        if (fromStatus === "escalated") {
          escalatedDelta -= 1
        }
        if (toStatus === "escalated") {
          escalatedDelta += 1
        }

        return {
          ...prev,
          unresolvedAlerts: Math.max(
            0,
            prev.unresolvedAlerts + unresolvedDelta
          ),
          resolvedAlerts: Math.max(0, prev.resolvedAlerts + resolvedDelta),
          escalatedAlerts: Math.max(0, prev.escalatedAlerts + escalatedDelta),
          // totalAlerts stays the same when moving between statuses
        }
      })
    },
    []
  )

  useEffect(() => {
    fetchStats()
  }, [])

  const refetch = async () => {
    await fetchStats()
  }

  return {
    loading,
    error,
    stats,
    refetch,
    updateStatsOptimistically,
  }
}

/**
 * Hook to get detailed information about a specific alert
 */
export function useAlertDetails(activationId: string | null) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [details, setDetails] = useState<any>(null)

  const fetchDetails = async (id: string) => {
    try {
      setLoading(true)
      setError(undefined)
      console.log(`🔄 Fetching details for alert ${id}...`)

      const alertDetails = await alertsService.getAlertDetails(id)
      setDetails(alertDetails)

      console.log(`✅ Successfully fetched details for alert ${id}`)
    } catch (err) {
      console.error(`❌ Error fetching alert details for ${id}:`, err)
      setError(
        err instanceof Error ? err.message : "Failed to load alert details"
      )
    } finally {
      setLoading(false)
    }
  }

  const refetchDetails = useCallback(async () => {
    if (activationId) {
      await fetchDetails(activationId)
    }
  }, [activationId])

  const addCommentOptimistically = useCallback(
    (comment: string, userId: string) => {
      if (!details) return

      const optimisticReview = {
        review_id: `temp-${Date.now()}`,
        activation_id: details.id,
        action_type: "comment",
        comment,
        user_id: userId,
        created_at: new Date().toISOString(),
        org_id: details.activation.org_id,
        assigned_to: null,
        escalation_level: null,
      }

      setDetails((prev: any) =>
        prev
          ? {
              ...prev,
              reviews: [...(prev.reviews || []), optimisticReview],
            }
          : prev
      )
    },
    [details]
  )

  useEffect(() => {
    if (activationId) {
      fetchDetails(activationId)
    } else {
      setDetails(null)
      setError(undefined)
      setLoading(false)
    }
  }, [activationId])

  return {
    loading,
    error,
    details,
    refetchDetails,
    addCommentOptimistically,
  }
}

/**
 * Combined hook for alerts data and stats with optimistic updates
 * This eliminates the need for callbacks between separate hooks
 */
export function useAlertsWithStats() {
  const [loading, setLoading] = useState(true)
  const [statsLoading, setStatsLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const [statsError, setStatsError] = useState<string | undefined>()
  const [data, setData] = useState<AlertsResponse | null>(null)
  const [stats, setStats] = useState<AlertsStats | null>(null)
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set())

  const fetchAlerts = async (skipLoadingState = false) => {
    try {
      if (!skipLoadingState) {
        setLoading(true)
      }
      setError(undefined)
      const alertsData = await alertsService.getAlerts()
      setData(alertsData)
    } catch (err) {
      console.error("Error fetching alerts:", err)
      setError(err instanceof Error ? err.message : "Failed to load alerts")
    } finally {
      if (!skipLoadingState) {
        setLoading(false)
      }
    }
  }

  const fetchStats = async (skipLoadingState = false) => {
    try {
      if (!skipLoadingState) {
        setStatsLoading(true)
      }
      setStatsError(undefined)
      const statsData = await alertsService.getAlertStats()
      setStats(statsData)
    } catch (err) {
      console.error("Error fetching alert stats:", err)
      setStatsError(
        err instanceof Error ? err.message : "Failed to load alert statistics"
      )
    } finally {
      if (!skipLoadingState) {
        setStatsLoading(false)
      }
    }
  }

  useEffect(() => {
    // Fetch both alerts and stats on mount
    Promise.all([fetchAlerts(), fetchStats()])
  }, [])

  const setItemUpdating = useCallback(
    (alertId: string, isUpdating: boolean) => {
      setUpdatingItems((prev) => {
        const newSet = new Set(prev)
        if (isUpdating) {
          newSet.add(alertId)
        } else {
          newSet.delete(alertId)
        }
        return newSet
      })
    },
    []
  )

  const updateAlertOptimistically = useCallback(
    (alertId: string, updates: Partial<AlertType>) => {
      setData((prev) => {
        if (!prev) return prev

        const updateAlertInList = (alerts: AlertType[]) =>
          alerts.map((alert) =>
            alert.id === alertId ? {...alert, ...updates} : alert
          )

        return {
          ...prev,
          unresolvedAlerts: updateAlertInList(prev.unresolvedAlerts),
          resolvedAlerts: updateAlertInList(prev.resolvedAlerts),
        }
      })
    },
    []
  )

  const moveAlertBetweenLists = useCallback(
    (
      alertId: string,
      fromStatus: AlertActivationStatus,
      toStatus: AlertActivationStatus,
      updates: Partial<AlertType> = {}
    ) => {
      // Update alerts data optimistically
      setData((prev) => {
        if (!prev) return prev

        // Find the alert in the appropriate list
        let alertToMove: AlertType | null = null
        let updatedUnresolved = [...prev.unresolvedAlerts]
        let updatedResolved = [...prev.resolvedAlerts]

        // Remove from source list
        if (fromStatus === "resolved" || fromStatus === "dismissed") {
          const index = updatedResolved.findIndex(
            (alert) => alert.id === alertId
          )
          if (index !== -1) {
            alertToMove = updatedResolved[index]
            updatedResolved.splice(index, 1)
          }
        } else {
          const index = updatedUnresolved.findIndex(
            (alert) => alert.id === alertId
          )
          if (index !== -1) {
            alertToMove = updatedUnresolved[index]
            updatedUnresolved.splice(index, 1)
          }
        }

        if (!alertToMove) {
          return prev
        }

        // Apply updates to the alert
        const updatedAlert: AlertType = {
          ...alertToMove,
          ...updates,
          status: toStatus,
        }

        // Add to target list
        if (toStatus === "resolved" || toStatus === "dismissed") {
          updatedResolved.unshift(updatedAlert) // Add to beginning of resolved list
        } else {
          updatedUnresolved.unshift(updatedAlert) // Add to beginning of unresolved list
        }

        return {
          ...prev,
          unresolvedAlerts: updatedUnresolved,
          resolvedAlerts: updatedResolved,
          unresolvedCount: updatedUnresolved.length,
          resolvedCount: updatedResolved.length,
        }
      })

      // Update stats optimistically at the SAME TIME
      setStats((prev) => {
        if (!prev) return prev

        let unresolvedDelta = 0
        let resolvedDelta = 0
        let escalatedDelta = 0

        // Determine if leaving unresolved status (anything that's not resolved/dismissed)
        if (fromStatus !== "resolved" && fromStatus !== "dismissed") {
          unresolvedDelta -= 1
        } else {
          // Leaving resolved status
          resolvedDelta -= 1
        }

        // Determine if entering resolved status
        if (toStatus === "resolved" || toStatus === "dismissed") {
          resolvedDelta += 1
        } else {
          // Entering unresolved status
          unresolvedDelta += 1
        }

        // Handle escalated status specifically
        if (fromStatus === "escalated") {
          escalatedDelta -= 1
        }
        if (toStatus === "escalated") {
          escalatedDelta += 1
        }

        return {
          ...prev,
          unresolvedAlerts: Math.max(
            0,
            prev.unresolvedAlerts + unresolvedDelta
          ),
          resolvedAlerts: Math.max(0, prev.resolvedAlerts + resolvedDelta),
          escalatedAlerts: Math.max(0, prev.escalatedAlerts + escalatedDelta),
          // totalAlerts stays the same when moving between statuses
        }
      })
    },
    []
  )

  const refetch = async () => {
    await Promise.all([fetchAlerts(), fetchStats()])
  }

  const refetchInBackground = async () => {
    await Promise.all([fetchAlerts(true), fetchStats(true)]) // Skip loading state to avoid UI flash
  }

  return {
    // Alerts data
    loading,
    error,
    data: data ? [...data.unresolvedAlerts, ...data.resolvedAlerts] : [],
    unresolvedAlerts: data?.unresolvedAlerts || [],
    resolvedAlerts: data?.resolvedAlerts || [],
    totalCount: data?.totalCount || 0,
    unresolvedCount: data?.unresolvedCount || 0,
    resolvedCount: data?.resolvedCount || 0,
    updatingItems,
    refetch,
    refetchInBackground,
    setItemUpdating,
    updateAlertOptimistically,
    moveAlertBetweenLists,

    // Stats data
    statsLoading,
    statsError,
    stats,
  }
}
